import QRCode from "react-qr-code";
import { useEffect, useState } from "react";
import QRCodeGenerator from "qrcode";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { BookingPdf } from "./OnlineBookingReceipt";
import { dateDuration, formatDate, formatDateForInput } from "../../utils/date";
import { padNumber } from "../../utils/string";
import Hotel from "../../models/Hotel";
import { getCurrentHostInfo, getUrl } from "../../utils/helper-function";

type Room = {
  number: string;
  price: number;
};

type Booking = {
  bookingNumber: string;
  rooms: Room[];
};

type Guest = {
  name: string;
  email: string;
  phone: string;
};

export type BookingSuccessData = {
  billId: string;
  guest: Guest;
  checkIn: string;
  checkOut: string;
  nights: number;
  bookings: Booking[];
};

export default function BookingSuccess() {

  const [qr, setQr] = useState("");
  const [hotel, setHotel] = useState({} as Hotel)
  const [data, setData] = useState({} as BookingSuccessData);

      const baseUrl = getUrl(true);

  useEffect(() => {
    const dataStr = localStorage.getItem("bookingPayload");

    if (!dataStr) return;

    const parsed = JSON.parse(dataStr);


    const { bookings, guest, hotel } = parsed;

    const firstBookedRoom = bookings?.[0]?.bookedRooms?.[0];

    if (!firstBookedRoom) return;

    const checkIn = firstBookedRoom.checkInDate;
    const checkOut = firstBookedRoom.checkOutDate;
    
    const mappedBookings: Booking[] = bookings.map((bk: any) => ({
      bookingNumber: bk.booking.bookingNo,
      rooms: bk.bookedRooms.map((r: any) => ({
        number: r.room.number,
        price: r.amount,
      })),
    }));

    const mappedData: BookingSuccessData = {
      guest,
      billId: parsed.billId,
      checkIn: formatDate(checkIn),
      checkOut: formatDate(checkOut),
      nights: dateDuration(checkIn, checkOut),
      bookings: mappedBookings,
    };

    setData(mappedData);
    setHotel(hotel);

    const qrCodeUrl = `${baseUrl}/check-in/${parsed.billId}`

    QRCodeGenerator.toDataURL(
      qrCodeUrl
    ).then(setQr);

  }, []);

  if (!data || !data.bookings) return null;

  const bookRefLists = data.bookings.map(bk => padNumber(Number(bk?.bookingNumber)));
  const bookRef = "WCHS-" + bookRefLists.join("-");

  const total =
    data?.bookings?.reduce((acc, booking) => {
      return (
        acc +
        booking.rooms.reduce((sum, r) => sum + r.price, 0)
      );
    }, 0) || 0;

  return (
    <>
      {/* PRINT STYLES */}
      <style>
        {`
          @media print {
            @page {
              margin: 10mm;
            }

            body {
              margin: 0;
              padding: 0;
              background: white !important;
            }

            .no-print {
              display: none !important;
            }

            .print-container {
              box-shadow: none !important;
              border-radius: 0 !important;
              max-width: 100% !important;
            }
          }
        `}
      </style>

      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="print-container max-w-3xl w-full bg-white rounded-2xl shadow-xl p-6 space-y-6">

          {/* HEADER */}
          <div className="text-center">
            <div className="text-green-500 text-5xl mb-2">✔</div>
            <h1 className="text-2xl font-semibold">Booking Successful</h1>
            <p className="text-gray-500 text-sm mt-1">
              Your reservation has been confirmed.
            </p>
          </div>

          {/* QR + BOOKING ID */}
          <div className="flex items-center justify-between border rounded-xl p-4">
            <div>
              <p className="text-gray-500 text-sm">Booking ID</p>
              <p className="text-lg font-bold text-blue-600">
                {"WCHS-" + bookRef}
              </p>
            </div>

            <QRCode
              // value={bookRef}
              value={`${baseUrl}/check-in/${data.billId}`}
              size={80}
            />
          </div>

          {/* GUEST INFO (FIXED INLINE TITLE + DATA) */}
          <div className="border rounded-xl p-4 bg-gray-50">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700 whitespace-nowrap">
                Guest Details
              </h2>
            </div>

            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Name</p>
                <p className="font-medium truncate">
                  {data?.guest?.name}
                </p>
              </div>

              {data?.guest?.email && <div>
                <p className="text-gray-500">Email</p>
                <p className="font-medium truncate">
                  {data?.guest?.email}
                </p>
              </div>}

              <div>
                <p className="text-gray-500">Phone</p>
                <p className="font-medium">
                  {data?.guest?.phone}
                </p>
              </div>
            </div>
          </div>

          {/* BOOKING INFO */}
          <div className="bg-gray-100 rounded-xl p-4 flex justify-between text-sm">
            <div>
              <p className="text-gray-500">Check-in</p>
              <p className="font-medium">{data.checkIn}</p>
            </div>

            <div>
              <p className="text-gray-500">Check-out</p>
              <p className="font-medium">{data.checkOut}</p>
            </div>

            <div>
              <p className="text-gray-500">Nights</p>
              <p className="font-medium">{data.nights}</p>
            </div>
          </div>

          {/* BOOKINGS */}
          <div>
            <h2 className="text-lg font-semibold mb-3">
              Booking References
            </h2>

            {(data.bookings ?? []).map((booking, idx) => (
              <div
                key={idx}
                className="border rounded-xl p-4 mb-4 shadow-sm"
              >
                <div className="flex justify-between mb-3">
                  <span className="text-gray-500 text-sm">
                    Booking Number
                  </span>
                  <span className="text-lg font-bold text-blue-600">
                    #{padNumber(Number(booking.bookingNumber))}
                  </span>
                </div>

                <div className="space-y-2">
                  {booking.rooms.map((room, i) => (
                    <div
                      key={i}
                      className="flex justify-between bg-gray-50 px-3 py-2 rounded-lg"
                    >
                      <span>Room {room.number}</span>
                      <span className="font-medium">
                        ₦{room.price.toLocaleString()} / {data.nights} night
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* TOTAL */}
          <div className="flex justify-between text-lg font-semibold border-t pt-4">
            <span>Total</span>
            <span>₦{total.toLocaleString()}</span>
          </div>

          {/* NOTE */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-sm text-yellow-800">
            Present this booking QR or booking ID at the hotel for verification.
          </div>

          {/* BUTTONS */}
          <div className="flex gap-3 no-print w-full">
            {/* PDF BUTTON */}
            <PDFDownloadLink
              document={<BookingPdf data={data} qr={qr} />}
              fileName={`booking_${formatDateForInput(new Date())}.pdf`}
              className="flex-1"
            >
              {({ loading }) => (
                <button
                  className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg py-2 text-sm font-medium"
                >
                  {loading ? "Generating..." : "Download PDF"}
                </button>
              )}
            </PDFDownloadLink>

            {/* DONE BUTTON */}
            <button
              onClick={() => window.location.href = "/rooms/search"}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2 text-sm font-medium"
            >
              Done
            </button>
          </div>

        </div>
      </div>
    </>
  );
}
